/**
 * Cookie consent management for GDPR compliance.
 *
 * Categories:
 * - essential: Auth tokens, session – always allowed (strictly necessary)
 * - analytics: PostHog tracking – requires consent
 * - functional: Chatwoot chat, theme preference cookie – requires consent
 */

export const CONSENT_COOKIE_NAME = 'extenshi-cookie-consent'
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

export interface CookieConsent {
	essential: true // always true, cannot be disabled
	analytics: boolean
	functional: boolean
}

const DEFAULT_CONSENT: CookieConsent = {
	essential: true,
	analytics: false,
	functional: false,
}

function getCookieDomain(): string {
	if (typeof window === 'undefined') return ''
	return location.hostname.endsWith('.extenshi.io') ? '; domain=.extenshi.io' : ''
}

export function getConsent(): CookieConsent | null {
	if (typeof document === 'undefined') return null
	const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE_NAME}=([^;]*)`))
	if (!match) return null
	try {
		const parsed = JSON.parse(decodeURIComponent(match[1]))
		return {
			essential: true,
			analytics: Boolean(parsed.analytics),
			functional: Boolean(parsed.functional),
		}
	} catch {
		return null
	}
}

export function setConsent(consent: CookieConsent): void {
	if (typeof document === 'undefined') return
	const value = encodeURIComponent(JSON.stringify(consent))
	const domain = getCookieDomain()
	// biome-ignore lint/suspicious/noDocumentCookie: need direct access for cross-subdomain consent cookie
	document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${CONSENT_MAX_AGE}; SameSite=Lax${domain}`
}

export function acceptAll(): CookieConsent {
	const consent: CookieConsent = { essential: true, analytics: true, functional: true }
	setConsent(consent)
	return consent
}

export function acceptEssentialOnly(): CookieConsent {
	const consent: CookieConsent = { ...DEFAULT_CONSENT }
	setConsent(consent)
	return consent
}

export function hasConsentDecision(): boolean {
	return getConsent() !== null
}

export function hasAnalyticsConsent(): boolean {
	return getConsent()?.analytics === true
}

export function hasFunctionalConsent(): boolean {
	return getConsent()?.functional === true
}
